import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import StatusChip from "components/StatusChip";
import Swal from "sweetalert2";
import { API_BASE, API_HOST } from "config/api";

const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  memberName: PropTypes.string,
  message: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
      <Box>
        <Typography variant="button" fontWeight={600} display="block">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {church?.email || "church@example.com"}
        </Typography>
      </Box>
    </Box>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const MessageCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value && value.length > 50 ? value.substring(0, 50) + "..." : value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </Typography>
  </Box>
);
MessageCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const STATUS_COLOR = {
  sent: "success",
  processed: "success",
  queued: "success",
};

const StatusCell = ({ value }) => (
  <StatusChip value={value} color={STATUS_COLOR[value] || "error"} />
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useMessageData() {
  const { user, church } = useAuth();
  const ChurchCell = createChurchCell(church);

  const [smsSummary, setSmsSummary] = useState({
    balance: 0,
    totalSent: 0,
    totalCost: 0,
    lastTransaction: null,
    isLoading: true,
  });

  const [messageData, setMessageData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Member",
        accessor: "memberName",
        align: "left",
        width: "20%",
        Cell: ChurchCell,
      },
      {
        Header: "Message",
        accessor: "message",
        align: "left",
        width: "30%",
        Cell: MessageCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "10%",
        Cell: StatusCell,
      },
      {
        Header: "Cost",
        accessor: "cost",
        align: "center",
        width: "10%",
        Cell: SerialCell,
      },
      {
        Header: "Date",
        accessor: "createdAt",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "15%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [members, setMembers] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Update SMS summary from cost_info response
  const updateSmsSummaryFromResponse = useCallback(
    (costInfo) => {
      if (costInfo) {
        const newBalance = costInfo.balance_after;
        const newTotalSent = smsSummary.totalSent + (costInfo.recipient_count || 1);
        const newTotalCost = smsSummary.totalCost + costInfo.total_cost;

        setSmsSummary((prev) => ({
          balance: newBalance,
          totalSent: prev.totalSent + (costInfo.recipient_count || 1),
          totalCost: prev.totalCost + costInfo.total_cost,
          lastTransaction: {
            cost: costInfo.total_cost,
            recipients: costInfo.recipient_count || 1,
            perMessage: costInfo.per_message,
            segments: costInfo.segments_per_message,
            balanceBefore: costInfo.balance_before,
            balanceAfter: costInfo.balance_after,
            timestamp: new Date().toISOString(),
          },
          isLoading: false,
        }));

        // Store in localStorage for persistence
        localStorage.setItem("sms_balance", newBalance);
        localStorage.setItem("sms_total_sent", newTotalSent.toString());
        localStorage.setItem("sms_total_cost", newTotalCost.toString());

        // Dispatch global event for dashboard to update
        const event = new CustomEvent("sms-balance-updated", {
          detail: {
            balance: newBalance,
            totalSent: newTotalSent,
            totalCost: newTotalCost,
            costInfo: costInfo,
          },
        });
        window.dispatchEvent(event);
        console.log("Dispatched sms-balance-updated event with balance:", newBalance);
      }
    },
    [smsSummary.totalSent, smsSummary.totalCost]
  );

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      setMessageData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/v3/sms/members/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const messages = result.data || result.messages || result;

      if (!Array.isArray(messages)) {
        throw new Error("API response is not an array");
      }

      // Calculate totals from messages
      const totalCost = messages.reduce((sum, msg) => sum + (parseFloat(msg.cost) || 0), 0);
      const totalSent = messages.length;

      // Update smsSummary with actual data
      setSmsSummary((prev) => ({
        ...prev,
        totalSent: totalSent,
        totalCost: totalCost,
        isLoading: false,
      }));

      const sortedMessages = [...messages].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setMessageData((prev) => ({
        ...prev,
        rows: sortedMessages.map((message, index) => ({
          id: message.id,
          serial: index + 1,
          memberName: message.member
            ? `${message.member.first_name} ${message.member.surname}`
            : "N/A",
          message: message.message,
          type: message.type,
          status: message.status || "pending",
          cost: message.cost ? `${message.cost} credits` : "-",
          createdAt: new Date(message.created_at || message.createdAt).toLocaleString(),
          originalData: message,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch messages failed:", error);
      setMessageData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  }, [user, church]);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/members/church/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setMembers(result.data || result.members || result);
    } catch (error) {
      console.error("Fetch members failed:", error);
    }
  }, [church?.id]);

  // Fetch SMS balance from API
  const fetchSmsBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/v3/sms/balance`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch balance");

      const result = await response.json();
      console.log("Balance API Response:", result);

      if (result.status === "success") {
        const data = result.data || result;
        setSmsSummary((prev) => ({
          ...prev,
          balance: data.balance || 0,
          totalSent: data.total_sent || prev.totalSent || 0,
          totalCost: data.total_spent || prev.totalCost || 0,
          isLoading: false,
        }));

        // Store in localStorage
        localStorage.setItem("sms_balance", data.balance || 0);
        localStorage.setItem("sms_total_sent", data.total_sent || 0);
        localStorage.setItem("sms_total_cost", data.total_spent || 0);
      }
    } catch (error) {
      console.error("Failed to fetch SMS balance:", error);
      // Try to get from localStorage as fallback
      const savedBalance = localStorage.getItem("sms_balance");
      const savedTotalSent = localStorage.getItem("sms_total_sent");
      const savedTotalCost = localStorage.getItem("sms_total_cost");

      if (savedBalance) {
        setSmsSummary((prev) => ({
          ...prev,
          balance: parseInt(savedBalance),
          totalSent: parseInt(savedTotalSent || "0"),
          totalCost: parseInt(savedTotalCost || "0"),
          isLoading: false,
        }));
      } else {
        setSmsSummary((prev) => ({ ...prev, isLoading: false }));
      }
    }
  }, []);

  // Send single SMS
  // Send single SMS
  const sendSingleSms = useCallback(
    async (messageData) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Please login to continue");

        // Make sure church.id is available
        if (!church?.id) {
          throw new Error("Church information not available");
        }

        const response = await fetch(`${API_BASE}/v3/sms/send`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            member_id: messageData.member_id,
            recipient: messageData.recipient,
            message: messageData.message,
            type: messageData.type || "single",
            status: "pending",
            church_id: church.id, // Add this line
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || `HTTP error! status: ${response.status}`);
        }

        // Update balance from response cost_info
        if (result.data?.cost_info) {
          updateSmsSummaryFromResponse(result.data.cost_info);

          Swal.fire({
            title: "Success!",
            html: `Message sent successfully!<br/>
               <strong>Cost:</strong> ${result.data.cost_info.total_cost} credit(s)<br/>
               <strong>Balance remaining:</strong> ${result.data.cost_info.balance_after} credits`,
            icon: "success",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            title: "Success!",
            text: "Message sent successfully!",
            icon: "success",
            confirmButtonText: "OK",
          });
        }

        await fetchMessages();
        return result;
      } catch (error) {
        console.error("Send SMS failed:", error);
        Swal.fire({
          title: "Error!",
          text: error.message,
          icon: "error",
          confirmButtonText: "OK",
        });
        throw error;
      }
    },
    [fetchMessages, updateSmsSummaryFromResponse, church?.id]
  );

  // Send bulk SMS
  // Send bulk SMS
  const sendBulkMessages = useCallback(
    async (recipients, message, type = "bulk") => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Please login to continue");

        // Make sure church.id is available
        if (!church?.id) {
          throw new Error("Church information not available");
        }

        const response = await fetch(`${API_BASE}/v3/sms/send-bulk`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            recipients,
            message,
            type: type,
            status: "pending",
            church_id: church.id, // Add this line
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || `HTTP error! status: ${response.status}`);
        }

        // Update balance from response cost_info
        if (result.data?.cost_info) {
          updateSmsSummaryFromResponse(result.data.cost_info);

          Swal.fire({
            title: "Success!",
            html: `Messages sent successfully to ${recipients.length} recipient(s)!<br/>
               <strong>Total cost:</strong> ${result.data.cost_info.total_cost} credits<br/>
               <strong>Balance remaining:</strong> ${result.data.cost_info.balance_after} credits`,
            icon: "success",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            title: "Success!",
            text: `Messages sent successfully to ${recipients.length} recipients!`,
            icon: "success",
            confirmButtonText: "OK",
          });
        }

        await fetchMessages();
        return result;
      } catch (error) {
        console.error("Bulk SMS failed:", error);
        Swal.fire({
          title: "Error!",
          text: error.message,
          icon: "error",
          confirmButtonText: "OK",
        });
        throw error;
      }
    },
    [fetchMessages, updateSmsSummaryFromResponse, church?.id]
  );

  // Handle edit submit
  const handleEditSubmit = useCallback(
    async (messageData) => {
      if (messageData.id) {
        // Update existing message
        try {
          const token = localStorage.getItem("authToken");
          if (!token) return;

          const response = await fetch(`${API_BASE}/v3/sms/send/${messageData.id}`, {
            method: "PUT",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              member_id: messageData.member_id,
              recipient: messageData.recipient,
              message: messageData.message,
              type: messageData.type,
              status: "pending",
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          Swal.fire({
            title: "Success!",
            text: "Message updated successfully!",
            icon: "success",
            confirmButtonText: "OK",
          });

          await fetchMessages();
          setEditModalOpen(false);
        } catch (error) {
          console.error("Update failed:", error);
          Swal.fire({
            title: "Error!",
            text: error.message,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } else {
        // Send new message
        await sendSingleSms(messageData);
        setEditModalOpen(false);
      }
    },
    [sendSingleSms, fetchMessages]
  );

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/v3/messages/${selectedMessage.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: "Message deleted successfully",
        icon: "success",
        confirmButtonText: "OK",
      });

      await fetchMessages();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedMessage, fetchMessages]);

  const handleEdit = useCallback((message) => {
    setSelectedMessage(message);
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((message) => {
    setSelectedMessage(message);
    setDeleteModalOpen(true);
  }, []);

  const handleAddMessage = useCallback(() => {
    setSelectedMessage({
      id: null,
      recipient: "",
      message: "",
      type: "single",
      status: "pending",
      member_id: null,
    });
    setEditModalOpen(true);
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchMessages();
    fetchMembers();
    fetchSmsBalance();
  }, [fetchMessages, fetchMembers, fetchSmsBalance]);

  return {
    ...messageData,
    members,
    smsSummary,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMessage,
    setSelectedMessage,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMessage,
    sendBulkMessages,
    sendSingleSms,
    fetchSmsBalance,
  };
}
